import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { updateAccountSettings } from '@/api/endpoints/settings';
import { writeDeviceSettings } from '@/api/storage/device-settings';
import {
  ACCOUNT_SETTINGS_DEFAULTS,
  DEVICE_SETTINGS_DEFAULTS,
  SETTINGS_QUERY_KEYS,
  accountSettingsQueryOptions,
  deviceSettingsQueryOptions,
  type AccountSettings,
  type DeviceSettings,
} from '@/api/queries/settings';
import { SETTING_KINDS, type SettingKey } from '../setting-kinds';

/**
 * The union of both settings documents. Because the account and device key sets
 * are disjoint, `AllSettings[K]` resolves to the correct value type per key.
 */
type AllSettings = AccountSettings & DeviceSettings;

const ALL_DEFAULTS: AllSettings = {
  ...ACCOUNT_SETTINGS_DEFAULTS,
  ...DEVICE_SETTINGS_DEFAULTS,
};

export interface UseSettingResult<K extends SettingKey> {
  value: AllSettings[K];
  setValue: (next: AllSettings[K]) => void;
  isLoading: boolean;
}

/**
 * The everyday settings hook: read one setting's value and set it optimistically.
 *
 * Kind-agnostic — the setting-kinds map picks the backing query (account vs
 * device). Reads are sliced via React Query `select`, so a `units` consumer only
 * re-renders when `units` changes. `setValue` writes the cache immediately, fires
 * the backing mutation (network for account, local write for device), commits the
 * authoritative result on success, and rolls the cache back on error.
 */
export function useSetting<K extends SettingKey>(key: K): UseSettingResult<K> {
  const queryClient = useQueryClient();
  const isAccount = SETTING_KINDS[key] === 'account';

  const accountQuery = useQuery({
    ...accountSettingsQueryOptions(),
    enabled: isAccount,
    select: (doc: AccountSettings) => doc[key as keyof AccountSettings],
  });

  const deviceQuery = useQuery({
    ...deviceSettingsQueryOptions(),
    enabled: !isAccount,
    select: (doc: DeviceSettings) => doc[key as keyof DeviceSettings],
  });

  const sliced = (isAccount ? accountQuery.data : deviceQuery.data) as
    | AllSettings[K]
    | undefined;
  const isLoading = isAccount ? accountQuery.isLoading : deviceQuery.isLoading;
  const value = sliced ?? (ALL_DEFAULTS[key] as AllSettings[K]);

  const setValue = useCallback(
    (next: AllSettings[K]) => {
      if (isAccount) {
        const queryKey = SETTINGS_QUERY_KEYS.account();
        const previous = queryClient.getQueryData<AccountSettings>(queryKey);
        const base = previous ?? ACCOUNT_SETTINGS_DEFAULTS;
        // Optimistic write.
        queryClient.setQueryData<AccountSettings>(queryKey, { ...base, [key]: next });
        updateAccountSettings({ [key]: next } as Partial<AccountSettings>)
          .then((server) => {
            queryClient.setQueryData<AccountSettings>(queryKey, server);
          })
          .catch((error) => {
            // Roll back to whatever the cache held before the optimistic write.
            queryClient.setQueryData<AccountSettings | undefined>(queryKey, previous);
            console.warn('Failed to update account setting, rolled back:', error);
          });
      } else {
        const queryKey = SETTINGS_QUERY_KEYS.device();
        const previous = queryClient.getQueryData<DeviceSettings>(queryKey);
        const base = previous ?? DEVICE_SETTINGS_DEFAULTS;
        queryClient.setQueryData<DeviceSettings>(queryKey, { ...base, [key]: next });
        writeDeviceSettings({ [key]: next } as Partial<DeviceSettings>)
          .then((doc) => {
            queryClient.setQueryData<DeviceSettings>(queryKey, doc);
          })
          .catch((error) => {
            queryClient.setQueryData<DeviceSettings | undefined>(queryKey, previous);
            console.warn('Failed to write device setting, rolled back:', error);
          });
      }
    },
    [isAccount, key, queryClient],
  );

  return { value, setValue, isLoading };
}
